import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Button } from "@/components/ui/Button";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import type { UserProfile } from "@/api/clubsApi";
import { cn } from "@/components/ui/cn";

interface EditIntroModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile;
}

const textareaClassName = cn(
  "w-full min-h-[120px] rounded-2xl border border-border-tertiary p-3",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary resize-none"
);

export function EditIntroModal({
  open,
  onOpenChange,
  profile,
}: EditIntroModalProps) {
  const [updateProfile, { isLoading: isSaving }] = useUpdateUserProfileMutation();

  // Form state
  const [bio, setBio] = useState("");

  // Initialize form state when modal opens
  useEffect(() => {
    if (open && profile) {
      setBio(profile.bio || "");
    }
  }, [open, profile]);

  const handleSave = async () => {
    try {
      await updateProfile({
        id: profile.id,
        bio: bio.trim() || null,
        updated_at: DateTime.now().toISO(),
      }).unwrap();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Edit intro" onClose={() => onOpenChange(false)} />

        <SidePanelBody className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Share a quick intro about yourself..."
              className={textareaClassName}
            />
          </div>
        </SidePanelBody>

        <SidePanelFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-text-primary text-text-reversed hover:bg-text-primary/90"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </SidePanelFooter>
      </SidePanelContent>
    </SidePanel>
  );
}
